// Returns the step value for zoom in based on current zoomLevel
export const getZoomStepIn = (zoomLevel: number) => {
  zoomLevel = Number(zoomLevel.toFixed(3));
  if (zoomLevel < 0.5) return 0; // Out of supported range (optional)
  if (zoomLevel < 1) return 0.1;
  if (zoomLevel < 2) return 0.25;
  if (zoomLevel <= 10) return 1;
  return 0; // Outside range, no increment (optional)
};

// Returns the step value for zoom out based on current zoomLevel
export const getZoomStepOut = (zoomLevel: number) => {
  zoomLevel = Number(zoomLevel.toFixed(3));
  if (zoomLevel <= 0.5) return 0; // Minimum limit
  if (zoomLevel <= 1) return 0.1;
  if (zoomLevel <= 2) return 0.25;
  if (zoomLevel <= 10) return 1;
  return 0;
};

// Smooth pinch-to-zoom + two-finger pan for touch devices.
// The Stage applies the scale (scaleX/scaleY) while the Layer holds the pan
// offset (its own x/y). To keep the pinch midpoint stationary we scale the
// stage and move the layer inversely, and we also translate the layer by the
// on-screen movement of the pinch center so two-finger drag pans naturally.

interface PinchContext {
  stageRef: any;
  zoomLevel: number;
  setZoomLevel: (v: number) => void;
  dynamicValuesEnumMaxZoom: number;
  dynamicValuesEnumMinZoom?: number;
  setNewValueOnStageMove?: (v: { x: number; y: number }) => void;
}

interface PinchState {
  dist: number;
  centerX: number;
  centerY: number;
  scale: number;
  layerX: number;
  layerY: number;
}

const getPinchDistance = (p1: any, p2: any) =>
  Math.hypot(p2.x - p1.x, p2.y - p1.y);

const getPinchCenter = (p1: any, p2: any) => ({
  x: (p1.x + p2.x) / 2,
  y: (p1.y + p2.y) / 2,
});

// Capture the initial distance/center when a second finger lands
export const handleStageTouchStart = (
  e: any,
  ctx: PinchContext,
  stateRef: any,
) => {
  const touches = e?.evt?.touches;
  if (!touches || touches.length < 2) return;

  const stage: any = ctx.stageRef?.current;
  if (!stage) return;

  e.evt.preventDefault();

  const rect = stage.container().getBoundingClientRect();
  const p1 = { x: touches[0].clientX - rect.left, y: touches[0].clientY - rect.top };
  const p2 = { x: touches[1].clientX - rect.left, y: touches[1].clientY - rect.top };
  const center = getPinchCenter(p1, p2);

  const layer = stage.getLayers?.()?.[0];

  stateRef.current = {
    dist: getPinchDistance(p1, p2),
    centerX: center.x,
    centerY: center.y,
    scale: ctx.zoomLevel,
    layerX: layer ? layer.x() : 0,
    layerY: layer ? layer.y() : 0,
  };

  // Prevent the layer from being dragged by Konva while pinching
  if (layer) layer.draggable(false);
};

// Update zoom/pan as the fingers move
export const handleStageTouchMove = (
  e: any,
  ctx: PinchContext,
  stateRef: any,
) => {
  const touches = e?.evt?.touches;
  const prev = stateRef?.current;
  if (!touches || touches.length < 2 || !prev) return;

  const stage: any = ctx.stageRef?.current;
  if (!stage) return;

  e.evt.preventDefault();

  const rect = stage.container().getBoundingClientRect();
  const p1 = { x: touches[0].clientX - rect.left, y: touches[0].clientY - rect.top };
  const p2 = { x: touches[1].clientX - rect.left, y: touches[1].clientY - rect.top };
  const center = getPinchCenter(p1, p2);
  const dist = getPinchDistance(p1, p2);

  if (!prev.dist) return;

  const minZoom = ctx.dynamicValuesEnumMinZoom ?? 0.5;
  const layer = stage.getLayers?.()?.[0];
  const oldScale = stage.scaleX() || ctx.zoomLevel;

  let newScale = oldScale * (dist / prev.dist);
  newScale = Math.max(minZoom, Math.min(ctx.dynamicValuesEnumMaxZoom, newScale));

  // Keep the content point under the initial pinch center fixed while the
  // fingers move (this also gives natural two-finger panning). With stage
  // position at 0, screen = scale * (layerPos + contentPoint), so:
  //   contentPoint = center / oldScale - layerPosAtStart
  //   newLayerPos  = newCenter / newScale - contentPoint
  const pivotX = prev.centerX / oldScale - prev.layerX;
  const pivotY = prev.centerY / oldScale - prev.layerY;

  const newLayerX = center.x / newScale - pivotX;
  const newLayerY = center.y / newScale - pivotY;

  if (layer) layer.position({ x: newLayerX, y: newLayerY });

  stage.scale({ x: newScale, y: newScale });
  stage.batchDraw ? stage.batchDraw() : stage.draw();

  ctx.setZoomLevel(Number(newScale.toFixed(3)));
  if (ctx.setNewValueOnStageMove) {
    ctx.setNewValueOnStageMove({
      x: Number(newLayerX.toFixed(2)),
      y: Number(newLayerY.toFixed(2)),
    });
  }

  stateRef.current = {
    dist,
    centerX: center.x,
    centerY: center.y,
    scale: newScale,
    layerX: newLayerX,
    layerY: newLayerY,
  };
};

// Restore layer dragging when the gesture ends
export const handleStageTouchEnd = (e: any, ctx: PinchContext, stateRef: any) => {
  const touches = e?.evt?.touches;
  if (touches && touches.length >= 2) return;

  stateRef.current = null;

  const stage: any = ctx.stageRef?.current;
  if (!stage) return;

  const layer = stage.getLayers?.()?.[0];
  if (layer) layer.draggable(true);
};

// Returns the three touch handlers (start/move/end) pre-bound to the given
// context, so each component only needs to call this once instead of
// duplicating the handler boilerplate.
export const createPinchZoomHandlers = (
  ctx: PinchContext,
  stateRef: any,
) => ({
  handleTouchStart: (e: any) => handleStageTouchStart(e, ctx, stateRef),
  handleTouchMove: (e: any) => handleStageTouchMove(e, ctx, stateRef),
  handleTouchEnd: (e: any) => handleStageTouchEnd(e, ctx, stateRef),
});

// Handle wheel zoom on Konva Stage — keeps the point under cursor stationary
export const handleStageWheel = (params: {
  e: any;
  stageRef: any;
  zoomLevel: number;
  setZoomLevel: (v: number) => void;
  newValueOnStageMove: { x: number; y: number };
  setNewValueOnStageMove: (v: { x: number; y: number }) => void;
  setTooltip: any;
  tooltip: any;
  setHoverState: (h: any) => void;
  hoverState: any;
  setGlobalSelectedShape: any;
  setSelectedWhileHovered: (s: any) => void;
  dynamicValuesEnumMaxZoom: number;
  getZoomStepInFn?: (zoomLevel: number) => number;
  getZoomStepOutFn?: (zoomLevel: number) => number;
}) => {
  const {
    e,
    stageRef,
    zoomLevel,
    setZoomLevel,
    newValueOnStageMove,
    setNewValueOnStageMove,
    setTooltip,
    tooltip,
    setHoverState,
    hoverState,
    setGlobalSelectedShape,
    setSelectedWhileHovered,
    dynamicValuesEnumMaxZoom,
    getZoomStepInFn = getZoomStepIn,
    getZoomStepOutFn = getZoomStepOut,
  } = params;

  e.evt.preventDefault();
  const stage: any = stageRef.current;
  if (!stage) return;

  const oldScale = zoomLevel;
  const pointer = stage.getPointerPosition();
  if (!pointer) return;

  const delta = e.evt.deltaY;
  const newScale =
    delta < 0
      ? Math.min(
          dynamicValuesEnumMaxZoom,
          zoomLevel + getZoomStepInFn(zoomLevel),
        )
      : Math.max(0.5, zoomLevel - getZoomStepOutFn(zoomLevel));

  if (newScale === oldScale) return;

  // Adjust layer position so the point under the cursor remains stationary
  // const factor = (1 / newScale - 1 / oldScale);
  // const newX = newValueOnStageMove.x + pointer.x * factor;
  // const newY = newValueOnStageMove.y + pointer.y * factor;

  // setNewValueOnStageMove({ x: newX, y: newY });
  setZoomLevel(newScale);

  if (setTooltip || tooltip) {
    setTooltip({ ...tooltip, visible: false });
  }

  setHoverState({ ...hoverState, visible: false });

  if (setGlobalSelectedShape) {
    setGlobalSelectedShape(null);
  }

  setSelectedWhileHovered(null);
};

// Handle delayed hover enter for a shape (shows hover UI after a short delay)
export const handleShapeMouseEnter = (params: any) => {
  const {
    e,
    shape,
    shapes,
    hoverTimeoutRef,
    HOVER_DELAY = 500,
    setHovered,
    setSelectedWhileHovered,
    setHoverState,
    newValueOnStageMove,
    zoomLevel,
    dynamicGatewayOffset,
    circlePositionOffset,
  } = params;

  if (hoverTimeoutRef?.current) {
    window.clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = null;
  }

  setHovered(true);
  try {
    e?.target?.getStage()?.container().style &&
      (e.target.getStage().container().style.cursor = "pointer");
  } catch (err) {
    // ignore
  }

  hoverTimeoutRef.current = window.setTimeout(() => {
    const selectedShape = shapes?.find((s: any) => s.id === shape?.id);
    setSelectedWhileHovered(selectedShape);

    if (e) {
      setHoverState({
        visible: true,
        x: e?.evt?.layerX - 18,
        y: e?.evt?.layerY - 20,
      });
    }
    // if (selectedShape) {
    //   const offset =
    //     (shape?.assetType?.toLowerCase() === "gateway"
    //       ? dynamicGatewayOffset
    //       : circlePositionOffset) || 0;
    //   const adjustedX =
    //     (selectedShape.x + newValueOnStageMove?.x + offset) * zoomLevel;
    //   const adjustedY =
    //     (selectedShape.y + newValueOnStageMove?.y + offset) * zoomLevel;

    //   setHoverState({ visible: true, x: adjustedX, y: adjustedY });
    // }
  }, HOVER_DELAY);
};

// Clear hover timer and hide hover UI
export const handleShapeMouseLeave = (params: any) => {
  const {
    e,
    hoverTimeoutRef,
    setHovered,
    setHoverState,
    setSelectedWhileHovered,
  } = params;
  try {
    e?.target?.getStage()?.container().style &&
      (e.target.getStage().container().style.cursor = "default");
  } catch (err) {
    // ignore
  }

  setHovered(false);
  if (hoverTimeoutRef?.current) {
    window.clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = null;
  }
  setHoverState({ visible: false, x: 0, y: 0 });
  setSelectedWhileHovered(null);
};

// handle image dimensions when height is more than width
export const calculateImageDimensions = (imageFile: any) => {
  const MAX_WIDTH = 889;
  const MAX_HEIGHT = 500;

  /* if (!imageFile || imageFile?.naturalWidth > imageFile?.naturalHeight) {
    return { width: MAX_WIDTH, height: MAX_HEIGHT };
  } */

  if (!imageFile) {
    return {
      width: MAX_WIDTH,
      height: MAX_HEIGHT,
      assetScale: { x: 1, y: 1 },
    };
  }

  const scale = Math.min(
    MAX_WIDTH / imageFile.naturalWidth,
    MAX_HEIGHT / imageFile.naturalHeight,
  );

  const scaledWidth = imageFile.naturalWidth * scale;
  const scaledHeight = imageFile.naturalHeight * scale;

  const assetScale =
    imageFile?.naturalWidth >= imageFile?.naturalHeight
      ? { x: scaledWidth / MAX_WIDTH, y: scaledHeight / MAX_HEIGHT }
      : { x: 1, y: 1 };

  return {
    width: scaledWidth,
    height: scaledHeight,
    assetScale: assetScale,
  };
};
